import styled, { useTheme } from 'styled-components';
import Modal, { FullHeightModalContent } from '../../../components/Modal';
import { BodyMedium, Heading5Text } from '../../../components/Texts';
import Button from '../../../components/Button';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import ApplePayMark from '../../../assets/icons/applepay.svg?react';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../redux/slices/modals';
import { useCallback, useState } from 'react';
import { useGiftForm } from '../../../components/GiftFormContext';
import { useSelector } from 'react-redux';
import { selectTokenByNetworkAndSymbol } from '../../../redux/slices/tokenSlice';
import {
  EVENT_APP_STORE_BUTTON_CLICKED,
  sendEvent,
} from '../../../common/analytics';
import { Capacitor } from '@capacitor/core';
import { QRCodeCanvas } from 'qrcode.react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px;
  width: 100%;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  margin: 8px 0;
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background-color: ${({ theme }) => theme.background.secondary};
  border-radius: 12px;
  width: 100%;
`;

const QRCodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
`;

const APP_STORE_URL = 'https://apps.apple.com/us/app/deks-xyz/id6749857801';

const ApplePayDownloadModal = ({ onClose, onPayWithCrypto, onCreateGift }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isCreatingGift, setIsCreatingGift] = useState(false);
  const { setGift } = useGiftForm();
  const isWeb = !Capacitor.isNativePlatform();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const isDesktopWeb = isWeb && !isMobile;

  // Get USDC token for default payment (used to pass to createGift function)
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const handleDownloadApp = useCallback(async () => {
    if (!onCreateGift) {
      console.error('onCreateGift function not provided');
      return;
    }

    setIsCreatingGift(true);

    try {
      // Use the provided createGift function (defaults to USDC for Apple Pay)
      const gift = await onCreateGift(usdcToken);

      if (gift) {
        setGift(gift);
      }

      // Send analytics event
      sendEvent(EVENT_APP_STORE_BUTTON_CLICKED);

      // Open App Store (on native and mobile web, desktop web shows QR code)
      if (!isDesktopWeb) {
        window.open(APP_STORE_URL, '_blank');
      }

      // Close modal
      dispatch(closeModal());
    } catch (error) {
      console.error('Failed to create gift:', error);
      setIsCreatingGift(false);
      // Still open App Store even if gift creation fails (on native and mobile web)
      sendEvent(EVENT_APP_STORE_BUTTON_CLICKED);
      if (!isDesktopWeb) {
        window.open(APP_STORE_URL, '_blank');
      }
      dispatch(closeModal());
    }
  }, [onCreateGift, usdcToken, setGift, dispatch]);

  const handlePayWithCrypto = useCallback(() => {
    dispatch(closeModal());
    if (onPayWithCrypto) {
      onPayWithCrypto();
    }
  }, [dispatch, onPayWithCrypto]);

  return (
    <Modal onClose={onClose}>
      <FullHeightModalContent>
        <Container>
          <IconContainer>
            <ApplePayMark
              style={{
                width: 60,
                height: 'auto',
              }}
            />
          </IconContainer>
          <ContentContainer>
            <Heading5Text>Pay with Apple Pay</Heading5Text>
            <BodyMedium
              style={{
                color: theme.text.secondary,
                maxWidth: '400px',
              }}
            >
              {isDesktopWeb
                ? 'Apple Pay is available in our iOS app. Scan the QR code with your phone to download the app, or continue with crypto.'
                : 'Apple Pay is available in our iOS app. Download the app to complete your payment, or continue with crypto.'}
            </BodyMedium>
          </ContentContainer>
          {isDesktopWeb && (
            <QRCodeContainer>
              <BodyMedium
                style={{
                  color: theme.text.secondary,
                  textAlign: 'center',
                }}
              >
                Scan with your phone to open App Store
              </BodyMedium>
              <QRCodeWrapper>
                <QRCodeCanvas value={APP_STORE_URL} size={200} />
              </QRCodeWrapper>
            </QRCodeContainer>
          )}
          <ButtonContainer>
            <Button
              fullWidth
              clickAnimation='bouncy'
              loading={isCreatingGift}
              onClick={handleDownloadApp}
            >
              {isDesktopWeb ? 'I\'ve Downloaded the App' : 'Download iOS App'}
            </Button>
            <Button
              fullWidth
              variant='secondary'
              clickAnimation='bouncy'
              onClick={handlePayWithCrypto}
              disabled={isCreatingGift}
            >
              Pay with Crypto
            </Button>
          </ButtonContainer>
        </Container>
      </FullHeightModalContent>
    </Modal>
  );
};

export default ApplePayDownloadModal;
